import React, { useEffect, useState } from "react";
import { Formik, Form, Field, useFormikContext } from "formik";
import CreatableSelect from "react-select";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../Appstore/store";
import { AddSmtpCampaigns, AddSmtpCampaignsMedia } from "../Features/smtpcampaign/Smtpcampaign";
import { IoMdArrowRoundBack } from "react-icons/io";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { IoDownloadOutline } from "react-icons/io5";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { GetallSmtpGateways } from "../Features/Smtpgatewaysdata";
import CustomEditor from "./CustomEditor";

interface FileUploadProps {
    field: any;
    form: any;
    label: string;
}

interface OptionType {
    value: number;
    label: string;
}

interface UploadResponse {
    status: string;
    message: string;
    fileUrl?: string;
}

export const stepOneSchema = Yup.object({
    name: Yup.string().required("Name is required").trim(),
    subject: Yup.string().required("Subject is required").trim(),
    is_numbers: Yup.boolean().default(true),
    emails: Yup.mixed()
        .nullable()
        .when("is_numbers", {
            is: true,
            then: (schema) =>
                Yup.array()
                    .min(1, "At least one file is required")
                    .test(
                        "fileType",
                        "Only .xlsx and .xls files are allowed",
                        (files) => {
                            if (!files || files.length === 0) return false;
                            return files.every(
                                (file) =>
                                    file &&
                                    file.name &&
                                    (file.name.endsWith(".xlsx") || file.name.endsWith(".xls"))
                            );
                        }
                    ),
        }),
    is_schedule: Yup.boolean(),
    scheduletime: Yup.string().when("is_schedule", {
        is: true,
        then: (schema) => Yup.string().required("Schedule time is required"),
    }),
    retries: Yup.string()
        .required("Retries is required")
        .min(1, "Retries must be at least 1"),
    smtpgatewayid: Yup.number().required("Please select an Smtp Gateway"),
    retry_minutes: Yup.number().min(1).max(60).required().label("Retry minutes"),
    send_report_emails: Yup.string()
        .test("emailList", "Invalid email format", (value) => {
            if (!value) return true;
            const emails = value.split(",").map((email) => email.trim());
            const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
            return emails.every((email, i) => emailRegex.test(email));
        })
        .test("duplicateEmail", "Duplicate email", (value) => {
            if (!value) return true;
            const emails = value.split(",").map((email) => email.trim());
            return emails.every((email, i) => emails.indexOf(email) === i);
        })
        .required()
        .label("Emails"),
});

export const stepTwoSchema = Yup.object({
    message: Yup.string().required("Message is required").trim(),
    reply_to: Yup.string().email("Invalid email format").trim().label("Reply to"),
    attachments: Yup.array().of(
        Yup.object({
            filename: Yup.string()
                .max(255)
                // .required()
                .label("File name"),
            path: Yup.string()
                .max(255)
                // .required()
                .label("File size"),
        })
    ),
    cc: Yup.string()
        .test("emailList", "Invalid email format", (value) => {
            if (!value) return true;
            const emails = value.split(",").map((email) => email.trim());
            const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
            return emails.every((email, i) => emailRegex.test(email));
        })
        .test("duplicateEmail", "Duplicate email", (value) => {
            if (!value) return true;
            const emails = value.split(",").map((email) => email.trim());
            return emails.every((email, i) => emails.indexOf(email) === i);
        })
        .label("CC"),
    bcc: Yup.string()
        .test("emailList", "Invalid email format", (value) => {
            if (!value) return true;
            const emails = value.split(",").map((email) => email.trim());
            const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
            return emails.every((email, i) => emailRegex.test(email));
        })
        .test("duplicateEmail", "Duplicate email", (value) => {
            if (!value) return true;
            const emails = value.split(",").map((email) => email.trim());
            return emails.every((email, i) => emails.indexOf(email) === i);
        })
        .label("BCC"),
});

const Addsmtpcampaign: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [step, setStep] = useState(1);
    const [stepOneData, setStepOneData] = useState({
        name: "",
        smtpgatewayid: null,
        subject: "",
        emails: [],
        scheduletime: "",
        retries: "",
        is_schedule: false,
        retry_minutes: "",
        send_report_emails: ""
    });

    useEffect(() => {
        dispatch(GetallSmtpGateways({ search: "", page: 1, limit: 10 }));
    }, [dispatch]);

    const { smtpgatewaysdata, loading } = useSelector(
        (state: RootState) => state.smtpgateways
    );
    const smtpgatewayOptions: OptionType[] = smtpgatewaysdata.map(
        (item: any) => ({
            value: item.id,
            label: item.gateway_name,
        })
    );

    const navigatetobackpage = () => {
        navigate(-1);
    };

    const FileUpload: React.FC<FileUploadProps> = ({ field, form, label }) => {
        const { setFieldValue } = form;

        const onDrop = (acceptedFiles: File[]) => {
            setFieldValue(field.name, acceptedFiles);
        };

        const handleRemoveFile = (index: number) => {
            const newFiles = field.value.filter((_: File, i: number) => i !== index);
            setFieldValue(field.name, newFiles);
        };

        const { getRootProps, getInputProps } = useDropzone({
            onDrop,
            accept: {
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
                    ".xlsx",
                ],
                "application/vnd.ms-excel": [".xls"],
            },
            multiple: false,
        });

        return (
            <>
                <label htmlFor="name">
                    Emails (Upload Excel File)
                    <span className="text-danger">*</span>
                </label>
                <div className="d-flex gap-2 justify-content-center align-items-center form-control ">
                    <div {...getRootProps()} className="dropzone flex-grow-1">
                        <input {...getInputProps()} />
                        <p
                            className={`shadow-none mb-0 ${form.touched.numbers && form.errors.numbers ? "is-invalid" : ""
                                }`}
                        >
                            Upload or drop a file right here
                        </p>
                    </div>
                    <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip>sample file</Tooltip>}
                    >
                        <a
                            type="button"
                            rel="noreferrer"
                            href={
                                process.env.REACT_APP_BASE_PATH +
                                "/assets/sample/Emails-sample-file.xlsx"
                            }
                            target="_blank"
                            download="Emails-sample-file"
                        >
                            <IoDownloadOutline
                                size={22}
                                data-bs-title="This top tooltip is themed via CSS variables."
                                data-bs-custom-class="custom-tooltip"
                                data-bs-toggle="tooltip"
                                data-bs-placement="bottom"
                            />
                        </a>
                    </OverlayTrigger>
                </div>
                {field.value && field.value.length > 0 && (
                    <div className="d-flex flex-wrap">
                        {field.value.map((file: File, index: number) => (
                            <div
                                key={index}
                                className="d-flex align-items-center mb-3 border p-2 mt-2"
                            >
                                <p className="mb-0">{file.name}</p>
                                <span className="ms-2" onClick={() => handleRemoveFile(index)}>
                                    <i
                                        className="fa-solid fa-square-xmark text-danger"
                                        style={{ cursor: "pointer" }}
                                    ></i>
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                {form.touched.emails && form.errors.emails && (
                    <div className="text-danger">{form.errors.emails}</div>
                )}
            </>
        );
    };


    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, i: number, setFieldValue: (field: string, value: any) => void) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("filename", file);

        try {
            const response = await dispatch(AddSmtpCampaignsMedia({ formData }));

            const payload = response.payload as { status: string; message: string; fileUrl?: string };

            if (payload.status === "success") {
                setFieldValue(`attachments[${i}].path`, payload.fileUrl || "");
                setFieldValue(`attachments[${i}].filename`, file.name);
                // toast.success(payload.message);
            } else {
                toast.error(payload.message || "Upload failed");
            }
        } catch (error) {
            toast.error("Image upload failed");
        }
    };


    return (
        <>
            <div className="wrapper">
                <div className="content-wrapper">
                    <section className="content">
                        <div className="container-fluid">
                            <div className="card mt-4">
                                <div className="card-header bg-white p-3 d-flex justify-content-md-between">
                                    <h2 className="w-100 mb-md-0 fs-3">Add SMTP Campaigns</h2>
                                    <Link to={""} className="text-decoration-none">
                                        <button
                                            onClick={navigatetobackpage}
                                            className="btn btn-info text-white d-flex align-items-center fw-semibold"
                                        >
                                            <IoMdArrowRoundBack /> Back
                                        </button>
                                    </Link>
                                </div>
                                <div className="card-body">
                                    <Formik
                                        initialValues={stepOneData}
                                        validationSchema={stepOneSchema}
                                        onSubmit={async (values, { setSubmitting }) => {
                                            setStepOneData(values);
                                            setStep(2);
                                        }}
                                    >
                                        {({
                                            values,
                                            errors,
                                            touched,
                                            handleChange,
                                            handleBlur,
                                            isSubmitting,
                                            setFieldValue,
                                        }) => (
                                            <Form className={`row ${step === 2 ? "d-none" : ""}`}>
                                                <div className="col-md-6">
                                                    <div className="form-group mb-3 ">
                                                        <label htmlFor="name">
                                                            Name
                                                            <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={`form-control shadow-none ${touched.name && errors.name
                                                                ? "is-invalid"
                                                                : ""
                                                                }`}
                                                            id="name"
                                                            name="name"
                                                            placeholder="Enter a Name"
                                                            value={values.name}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {touched.name && errors.name && (
                                                            <div className="text-danger">{errors.name}</div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="form-group mb-3 ">
                                                        <label htmlFor="subject">
                                                            Subject
                                                            <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={`form-control shadow-none ${touched.subject && errors.subject
                                                                ? "is-invalid"
                                                                : ""
                                                                }`}
                                                            id="subject"
                                                            name="subject"
                                                            placeholder="Enter a Subject"
                                                            value={values.subject}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {touched.subject && errors.subject && (
                                                            <div className="text-danger">
                                                                {errors.subject}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="form-group mb-3">
                                                        <label htmlFor="smtpgatewayid">
                                                            Smtp Gateway
                                                            <span className="text-danger">*</span>
                                                        </label>
                                                        <CreatableSelect
                                                            name="smtpgatewayid"
                                                            options={smtpgatewayOptions}
                                                            isLoading={loading}
                                                            placeholder="Search and select users"
                                                            classNamePrefix="select"
                                                            className={`shadow-none ${touched.smtpgatewayid && errors.smtpgatewayid
                                                                ? "is-invalid border-danger"
                                                                : ""
                                                                }`}
                                                            value={smtpgatewayOptions.find(
                                                                (option) =>
                                                                    option.value === values.smtpgatewayid
                                                            )} // Ensure correct value is selected
                                                            onChange={
                                                                (selectedOption: OptionType | null) =>
                                                                    setFieldValue(
                                                                        "smtpgatewayid",
                                                                        selectedOption
                                                                            ? selectedOption.value
                                                                            : null
                                                                    ) // Set only the ID value
                                                            }
                                                            formatOptionLabel={(option: OptionType) => (
                                                                <div>
                                                                    <p className="fw-semibold mb-0">
                                                                        {option.label}
                                                                    </p>{" "}
                                                                </div>
                                                            )}
                                                        />
                                                        {touched.smtpgatewayid &&
                                                            errors.smtpgatewayid && (
                                                                <div className="text-danger">
                                                                    {errors.smtpgatewayid as string}
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="form-group mb-3">
                                                        <label htmlFor="retries">
                                                            Retries<span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className={`form-control shadow-none ${touched.retries && errors.retries
                                                                ? "is-invalid"
                                                                : ""
                                                                }`}
                                                            id="retries"
                                                            name="retries"
                                                            placeholder="Enter a Retries Number"
                                                            value={values.retries}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {touched.retries && errors.retries && (
                                                            <div className="text-danger">
                                                                {errors.retries}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="container-fluid">
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <div className="form-group mb-3 ">
                                                                <label htmlFor="retry_minutes">
                                                                    Retry Minutes
                                                                    <span className="text-danger">*</span>
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    className={`form-control shadow-none ${touched.retry_minutes && errors.retry_minutes
                                                                        ? "is-invalid"
                                                                        : ""
                                                                        }`}
                                                                    id="retry_minutes"
                                                                    name="retry_minutes"
                                                                    placeholder="Enter a retry minutes"
                                                                    value={values.retry_minutes}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                />
                                                                {touched.retry_minutes &&
                                                                    errors.retry_minutes && (
                                                                        <div className="text-danger">
                                                                            {errors.retry_minutes}
                                                                        </div>
                                                                    )}
                                                            </div>

                                                            <div className="form-group mb-3">
                                                                <Field name="emails" component={FileUpload} />
                                                            </div>
                                                        </div>

                                                        <div className="col-md-6">
                                                            <div className="form-group mb-3">
                                                                <label htmlFor="send_report_emails">
                                                                    Emails <small>{"("}This email receives SMTP report for this campaign.{")"}</small><span className="text-danger">*</span>
                                                                </label>
                                                                <textarea
                                                                    style={{ height: 128, resize: "none" }}
                                                                    className={`form-control shadow-none ${touched.send_report_emails && errors.send_report_emails
                                                                        ? "is-invalid"
                                                                        : ""
                                                                        }`}
                                                                    id="send_report_emails"
                                                                    name="send_report_emails"
                                                                    placeholder="Enter Name"
                                                                    value={values.send_report_emails}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                />
                                                                {touched.send_report_emails && errors.send_report_emails && (
                                                                    <div className="text-danger">{errors.send_report_emails}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                </div>

                                                <div className="form-group mb-3 col-md-6">
                                                    <div className="d-flex gap-2 user-select-none">
                                                        <input
                                                            type="checkbox"
                                                            checked={values.is_schedule}
                                                            onChange={(e) =>
                                                                setFieldValue("is_schedule", e.target.checked)
                                                            }
                                                            className="mb-2"
                                                            id="show_schedule"
                                                        />
                                                        <label htmlFor="show_schedule">
                                                            Schedule Campaign ?
                                                        </label>
                                                    </div>

                                                    {values.is_schedule ? (
                                                        <input
                                                            type="datetime-local"
                                                            className={`form-control shadow-none ${touched.scheduletime && errors.scheduletime
                                                                ? "is-invalid"
                                                                : ""
                                                                }`}
                                                            id="scheduletime"
                                                            name="scheduletime"
                                                            value={values.scheduletime}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                    ) : null}

                                                    {touched.scheduletime && errors.scheduletime && (
                                                        <div className="text-danger">
                                                            {errors.scheduletime as string}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="d-flex">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary me-2"
                                                        disabled={isSubmitting}
                                                        style={{ minWidth: 120 }}
                                                    >
                                                        {isSubmitting ? (
                                                            <>
                                                                <span
                                                                    className="spinner-border spinner-border-sm"
                                                                    role="status"
                                                                    aria-hidden="true"
                                                                ></span>
                                                                <span className="ps-2">Next...</span>
                                                            </>
                                                        ) : (
                                                            "Next"
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => navigate(-1)}
                                                        style={{ minWidth: 120 }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                    <Formik
                                        initialValues={{
                                            cc: "",
                                            bcc: "",
                                            reply_to: "",
                                            message: "",
                                            attachments: [
                                                { filename: "", path: "" },
                                                { filename: "", path: "" },
                                                { filename: "", path: "" },
                                                { filename: "", path: "" },
                                            ] as { filename: string, path: string }[],
                                            isAttachments: false,
                                        }}
                                        validationSchema={stepTwoSchema}
                                        enableReinitialize={true}
                                        onSubmit={async (values, { setSubmitting }) => {
                                            const SmtpCampaignListData: any = {
                                                ...stepOneData,
                                                ...values,
                                            };
                                            try {
                                                const response: any = await dispatch(
                                                    AddSmtpCampaigns({ addData: SmtpCampaignListData })
                                                );
                                                if (
                                                    response.payload &&
                                                    response.payload.status === "success"
                                                ) {
                                                    toast.success(response.payload.message);
                                                    navigate(`/admin/smtp-campaigns`);
                                                } else {
                                                    toast.error(response.payload.message);
                                                }
                                            } finally {
                                                setSubmitting(false);
                                            }
                                        }}
                                    >
                                        {({
                                            values,
                                            errors,
                                            touched,
                                            handleChange,
                                            handleBlur,
                                            isSubmitting,
                                            setFieldValue,
                                        }) => (
                                            <Form className={`row ${step === 1 ? "d-none" : ""}`}>
                                                <div className="col-md-6 col-lg-4">
                                                    <div className="form-group mb-3 ">
                                                        <label htmlFor="CC">CC</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control shadow-none ${touched.cc && errors.cc ? "is-invalid" : ""
                                                                }`}
                                                            id="CC"
                                                            name="cc"
                                                            value={values.cc}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {touched.cc && errors.cc && (
                                                            <div className="text-danger">{errors.cc}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6 col-lg-4">
                                                    <div className="form-group mb-3 ">
                                                        <label htmlFor="bcc">Bcc</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control shadow-none ${touched.bcc && errors.bcc ? "is-invalid" : ""
                                                                }`}
                                                            id="bcc"
                                                            name="bcc"
                                                            value={values.bcc}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {touched.bcc && errors.bcc && (
                                                            <div className="text-danger">{errors.bcc}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6 col-lg-4">
                                                    <div className="form-group mb-3 ">
                                                        <label htmlFor="reply_to">Reply To</label>
                                                        <input
                                                            type="text"
                                                            className={`form-control shadow-none ${touched.reply_to && errors.reply_to
                                                                ? "is-invalid"
                                                                : ""
                                                                }`}
                                                            id="reply_to"
                                                            name="reply_to"
                                                            value={values.reply_to}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                        />
                                                        {touched.reply_to && errors.reply_to && (
                                                            <div className="text-danger">
                                                                {errors.reply_to}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="col-12">
                                                    <div className="custom-editor h-100 w-100 form-group mb-3">
                                                        <CustomEditor
                                                            setFieldValue={setFieldValue}
                                                            formikKey="message"
                                                            value={values.message}
                                                            placeholder="Write here"
                                                        />
                                                        {touched.message && errors.message && (
                                                            <div className="text-danger">
                                                                {errors.message}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="col-12">
                                                    <div className="form-group mb-3">
                                                        <div className="d-flex gap-2 user-select-none">
                                                            <input
                                                                type="checkbox"
                                                                checked={values.isAttachments}
                                                                onChange={(e) =>
                                                                    setFieldValue(
                                                                        "isAttachments",
                                                                        e.target.checked
                                                                    )
                                                                }
                                                                className="mb-2"
                                                                id="isAttachments"
                                                            />
                                                            <label htmlFor="isAttachments">
                                                                Include file attachments ?
                                                            </label>
                                                        </div>
                                                        {values.isAttachments ? (
                                                            <div className="form-group mb-3 ">
                                                                {Array.from({ length: 4 }).map((_, i) => (
                                                                    <div className="row">
                                                                        <div className="col-4">
                                                                            <label htmlFor={`attachments[${i}].filename`}>File name #{i + 1}</label>
                                                                            <input
                                                                                type="text"
                                                                                className={`form-control shadow-none ${touched?.attachments && touched?.attachments[i]?.filename &&
                                                                                    errors.attachments && errors.attachments[i] && JSON.parse(errors?.attachments[i]?.toString())?.filename
                                                                                    ? "is-invalid"
                                                                                    : ""
                                                                                    }`}
                                                                                id={`attachments[${i}].filename`}
                                                                                name={`attachments[${i}].filename`}
                                                                                value={values.attachments?.[i]?.filename}
                                                                                onChange={handleChange}
                                                                                onBlur={handleBlur}
                                                                            />
                                                                            {touched?.attachments && touched?.attachments[i]?.filename &&
                                                                                errors.attachments && (JSON.parse(errors.attachments[i].toString()))(
                                                                                    <div className="text-danger">
                                                                                        {JSON.parse(errors?.attachments[i]?.toString())?.filename}
                                                                                    </div>
                                                                                )}
                                                                        </div>

                                                                        <div className="col-8">
                                                                            <input
                                                                                type="file"
                                                                                id={`fileUpload-${i}`}
                                                                                className="d-none"
                                                                                accept="image/*"
                                                                                onChange={(event) => handleFileUpload(event, i, setFieldValue)}
                                                                            />
                                                                            <label htmlFor={`attachments[${i}].path`}>
                                                                                File Url #{i + 1}
                                                                            </label>
                                                                            <div className="input-group mb-3">
                                                                                <label className="me-2 input-group-text cursor-pointer" htmlFor={`fileUpload-${i}`}>
                                                                                    Upload {`${i + 1}`}
                                                                                </label>
                                                                                <input
                                                                                    type="text"
                                                                                    className={`form-control shadow-none rounded-end-1 ${touched?.attachments && touched?.attachments[i]?.path &&
                                                                                        errors.attachments && errors.attachments[i] && JSON.parse(errors?.attachments[i]?.toString())?.path
                                                                                        ? "is-invalid"
                                                                                        : ""
                                                                                        }`}
                                                                                    id={`attachments[${i}].path`}
                                                                                    name={`attachments[${i}].path`}
                                                                                    onChange={handleChange}
                                                                                    onBlur={handleBlur}
                                                                                    value={values.attachments?.[i]?.path || ""}
                                                                                />
                                                                            </div>
                                                                            {touched?.attachments && touched?.attachments[i]?.path &&
                                                                                errors.attachments && (JSON.parse(errors.attachments[i].toString()))(
                                                                                    <div className="text-danger">
                                                                                        {JSON.parse(errors?.attachments[i]?.toString())?.path}
                                                                                    </div>
                                                                                )}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>

                                                <div className="d-flex">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary me-2"
                                                        disabled={isSubmitting}
                                                        style={{ minWidth: 120 }}
                                                    >
                                                        {isSubmitting ? (
                                                            <>
                                                                <span
                                                                    className="spinner-border spinner-border-sm"
                                                                    role="status"
                                                                    aria-hidden="true"
                                                                ></span>
                                                                <span className="ps-2">Submiting...</span>
                                                            </>
                                                        ) : (
                                                            "Submit"
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => setStep(1)}
                                                        style={{ minWidth: 120 }}
                                                    >
                                                        Previous
                                                    </button>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default Addsmtpcampaign;
